import { SOSSIETY_RED, SOSSIETY_RED_HOVER } from '@/styles/brand';
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useI18n } from '@/contexts/I18nContext'
import { useAgendaConfig } from '@/features/calendar/hooks/useAgendaConfig'
import { TopNavigation } from '@/components/layout/TopNavigation'

const C = {
  bg:      '#160C0C',
  surface: '#281B1B',
  border:  '#FFFFFF0D',
  muted:   '#8D8D8D',
  accent:  SOSSIETY_RED,
}

const DURATION_OPTIONS = [30, 45, 60, 90, 120]

const Duration = () => {
  const navigate = useNavigate()
  const { t }    = useI18n()

  const { config, isLoading, updateConfig, isUpdating } = useAgendaConfig()

  const [duration, setDuration] = useState(60)
  const [customInput, setCustomInput] = useState('')
  const [synced,   setSynced]   = useState(false)
  const [saved,    setSaved]    = useState(false)

  // Sync from fetched config once
  if (!synced && !isLoading && config) {
    const d = config.default_duration ?? 60
    setDuration(d)
    // Pre-fill custom input if the saved value isn't a preset
    if (!DURATION_OPTIONS.includes(d)) setCustomInput(String(d))
    setSynced(true)
  }

  const isCustomSelected = !DURATION_OPTIONS.includes(duration)

  const handleCustomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    setCustomInput(raw)
    const n = parseInt(raw)
    if (!isNaN(n) && n > 0 && n <= 480) setDuration(n)
  }

  const handleSave = async () => {
    await updateConfig({ default_duration: duration })
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: C.bg }}>
      <TopNavigation />

      <div className="pt-16">
        {/* Header */}
        <div className="relative flex items-center px-4 py-3.5 border-b border-[#281B1B]">
          <button
            onClick={() => navigate(-1)}
            className="p-1 text-white/80 hover:text-white transition-colors z-10">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 text-base font-semibold truncate max-w-[60%]">
            {t('instructor_tools.edit_duration')}
          </h1>
        </div>

        <div className="px-5 pt-6">
          <p className="text-sm font-medium text-white mb-1">
            {t('instructor_tools.default_duration')}
          </p>
          <p className="text-xs mb-6" style={{ color: C.muted }}>
            {t('instructor_tools.default_duration_subtitle')}
          </p>

          {/* Preset pills */}
          <div className="flex gap-3 flex-wrap">
            {DURATION_OPTIONS.map(d => (
              <button
                key={d}
                onClick={() => { setDuration(d); setCustomInput('') }}
                className="px-5 py-3 rounded-full text-sm font-medium transition-colors"
                style={
                  duration === d
                    ? { backgroundColor: C.accent, color: '#fff' }
                    : { backgroundColor: C.surface, color: C.muted, border: `1px solid ${C.border}` }
                }
              >
                {d} {t('instructor_tools.min')}
              </button>
            ))}
          </div>

          {/* Custom duration input */}
          <div className="mt-5">
            <p className="text-xs mb-2" style={{ color: C.muted }}>
              {t('instructor_tools.custom_duration') || 'Custom (min)'}
            </p>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min="1"
                max="480"
                inputMode="numeric"
                placeholder="e.g. 75"
                value={customInput}
                onChange={handleCustomChange}
                className="w-32 rounded-xl px-4 py-3 text-sm text-white border focus:outline-none focus:ring-1 focus:ring-sossiety-red"
                style={{
                  backgroundColor: C.surface,
                  borderColor: isCustomSelected ? C.accent : 'rgba(255,255,255,0.15)',
                }}
              />
              <span className="text-sm" style={{ color: C.muted }}>
                {t('instructor_tools.min')}
              </span>
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={isUpdating}
            className="w-full mt-8 rounded-full py-3 text-sm font-semibold text-white disabled:opacity-40 transition-opacity"
            style={{ backgroundColor: C.accent }}
          >
            {isUpdating
              ? t('instructor_tools.saving')
              : saved
                ? t('instructor_tools.saved')
                : t('instructor_tools.save_config')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Duration
